import { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import RdvDetails from "./RdvDetails";
import { booleanFields } from "../../constants/templateVariants";

export const PlanningView = ({ rdvs, onDeleteRequest }) => {
  const [selectedRdv, setSelectedRdv] = useState(null);

  const now = new Date();

  // Trier tous les RDVs par date + heure
  const sortedRdvs = rdvs
    // on garde les rdvs max 5 jours avant
    .filter(
      (rdv) =>
        new Date(rdv.date_prise_rdv) >=
        new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
    )
    // et on les trie par date croissante
    .sort((a, b) => new Date(a.date_prise_rdv) - new Date(b.date_prise_rdv));

  // Trouver le premier RDV futur pour mettre en avant
  const firstFutureIndex = sortedRdvs.findIndex(
    (r) => new Date(r.date_prise_rdv) >= now
  );

  // Déplacer le premier RDV futur en haut de la liste
  const orderedRdvs =
    firstFutureIndex > 0
      ? [
          ...sortedRdvs.slice(firstFutureIndex),
          ...sortedRdvs.slice(0, firstFutureIndex),
        ]
      : sortedRdvs;

  // Groupement par date
  const grouped = orderedRdvs.reduce((acc, event) => {
    const date = new Date(event.date_prise_rdv);
    const label = date.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const key = date.toISOString().split("T")[0];
    if (!acc[key]) acc[key] = { label, rdvs: [] };
    acc[key].rdvs.push(event);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="p-4">
      <div className="timeline position-relative">
        {sortedDates.map((dateKey) => {
          const { label, rdvs: eventsOfDay } = grouped[dateKey];

          return (
            <div key={dateKey} className="mb-5">
              {/* Label de date */}
              <h5 className="text-primary text-capitalize mb-4 fw-semibold text-center">
                {label}
              </h5>

              <div className="position-relative">
                {/* Ligne centrale verticale */}
                <div
                  className="position-absolute top-0 start-50"
                  style={{
                    width: "2px",
                    backgroundColor: "#0d6efd",
                    height: "100%",
                    transform: "translateX(-50%)",
                    zIndex: 1,
                  }}
                />

                {/* RDV */}
                {eventsOfDay.map((event, idx) => {
                  const isPast = new Date(event.date_prise_rdv) < now;
                  const side = idx % 2 === 0;
                  const dotColor = isPast ? "#6c757d" : "#0d6efd";

                  return (
                    <div
                      key={event.id}
                      className="position-relative d-flex w-100 justify-content-between mb-4"
                    >
                      {/* Point sur la timeline */}
                      <div
                        className="position-absolute top-0"
                        style={{
                          left: "50%",
                          width: "20px",
                          height: "20px",
                          backgroundColor: dotColor,
                          borderRadius: "50%",
                          transform: "translate(-50%, 150%)",
                          zIndex: 2,
                        }}
                      />

                      {/* Carte RDV */}
                      <div
                        className={`card shadow-sm p-3 ${
                          isPast ? "text-muted opacity-50" : ""
                        }`}
                        style={{
                          width: "48%",
                          marginLeft: side ? 0 : "auto",
                          marginRight: side ? "auto" : 0,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <strong>
                            {event.client_nom} {event.client_prenom}
                            <span className="d-none d-md-inline">
                              {" "}
                              ({event.client_tel})
                            </span>
                          </strong>
                        </div>

                        <div>
                          {booleanFields.some((field) =>
                            Boolean(event[field])
                          ) && (
                            <div className="bg-body rounded shadow-sm">
                              <div className="d-flex gap-1 flex-wrap">
                                {booleanFields.map((field) =>
                                  event[field] ? (
                                    <div key={field} className="">
                                      <div className="badge bg-success-subtle text-success-emphasis rounded p-2">
                                        <strong>{formatKey(field)}</strong>
                                      </div>
                                    </div>
                                  ) : null
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-1">
                          Heure du Rdv :{" "}
                          {new Date(event.date_prise_rdv).toLocaleTimeString(
                            "fr-FR",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>

                        <div className="mt-2 d-flex justify-content-end align-items-center">
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => setSelectedRdv(event)}
                              title="Voir détails"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() =>
                                onDeleteRequest && onDeleteRequest(event)
                              }
                              title="Supprimer"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal détails RDV */}
      {selectedRdv && (
        <Modal show onHide={() => setSelectedRdv(null)} centered size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Détails du rendez-vous</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RdvDetails rdv={selectedRdv} />
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              onClick={() => {
                setSelectedRdv(null);
                onDeleteRequest(selectedRdv);
              }}
            >
              Supprimer
            </Button>
            <Button variant="secondary" onClick={() => setSelectedRdv(null)}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
